/**
 * vendorPhone.js — one definition of "is this a usable vendor phone number".
 *
 * A vendor phone is how the business calls the supplier back about a bad delivery. A
 * truncated one ("7") is worse than none at all: it looks answered, so nobody ever fills
 * it in. Every place that judges a phone number — the input, the submit gate, the confirm
 * popup, the OCR autofill — asks this module, so they can never disagree.
 *
 * The backend enforces the same rule independently (middleware/validation.js); this is the
 * copy that can explain itself to the user, not the one that guarantees anything.
 */

/** Longest input we keep. Past this it is not a mistyped number, it is not a number. */
const MAX_KEPT_DIGITS = 15;

/**
 * Digits only, with a RECOGNISED dialling prefix removed — and nothing else.
 *
 * This used to be `digits.slice(-10)`: keep the last ten of whatever arrives. That handles
 * "+91 98765 43210" but it also silently turned 891999730812259 into 9730812259 and called
 * it valid — the user pasted fifteen digits and the form accepted an invented ten, which is
 * precisely the wrong number in the field that exists to be dialled. Nothing auto-corrects
 * silently; over-long input is KEPT so the validator can say how many digits it saw.
 *
 * Only two prefixes are stripped, and only when doing so leaves exactly ten digits:
 *   91xxxxxxxxxx  India country code (with or without the +)
 *   0xxxxxxxxxx   the old STD trunk prefix, still typed out of habit
 */
export const normalizeVendorPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length <= 10) return digits;

  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);

  // Not a prefix this app recognises. Keep it, flag it, let the user decide.
  return digits.slice(0, MAX_KEPT_DIGITS);
};

/** Indian mobile numbers are exactly 10 digits and begin 6/7/8/9. */
export const isValidVendorPhone = (value) => /^[6-9]\d{9}$/.test(normalizeVendorPhone(value));

/**
 * Is this number already beyond saving, or just unfinished?
 *
 * The warning used to wait for the field to lose focus, which is right for a HALF-TYPED
 * number — scolding someone on the third digit teaches them nothing. It is wrong for a
 * number that no further typing can rescue: at fifteen digits the person is still typing
 * into a field that has been invalid for five keystrokes and says nothing, and they only
 * find out at submit.
 *
 * Two states are terminal, and only two:
 *   • more than ten digits — the next keystroke can only make it worse
 *   • a first digit outside 6-9 — an Indian mobile can never start that way
 *
 * "Too short" is deliberately NOT terminal: it is the normal state of a number being typed.
 *
 * UI-only, so it has no counterpart in backend/utils/vendorPhone.js — the server judges a
 * finished submission, where there is no such thing as "still typing".
 */
export const isTerminalVendorPhoneError = (value) => {
  const digits = normalizeVendorPhone(value);
  if (!digits) return false;
  if (digits.length > 10) return true;
  if (!/^[6-9]/.test(digits)) return true;
  return false;
};

/**
 * Why a phone is unusable, phrased for the person typing it. Null when it is fine, and
 * null when it is empty — "you haven't typed anything yet" is not an error to shout.
 */
export const vendorPhoneError = (value) => {
  const digits = normalizeVendorPhone(value);
  if (!digits) return null;
  // The leading digit is judged FIRST. It is knowable from one keystroke and it is the
  // actionable fact; telling someone who typed "5" that they are nine digits short sends
  // them off to type nine more that were never going to help.
  if (!/^[6-9]/.test(digits)) {
    return 'Indian mobile numbers start with 6, 7, 8 or 9.';
  }
  if (digits.length !== 10) {
    return `Phone number must be exactly 10 digits — ${digits.length} entered.`;
  }
  return null;
};
